import { useEffect, useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  CircularProgress,
  Box,
  IconButton,
  Popover,
  Chip,
  Stack,
  Tooltip,
} from '@mui/material';
import { getNewsletterHistory } from '../../services/user_Service';
import CustomSnackbar from '../../CustomSnackbar';
import { timeAgo } from '../../../utils/TimeFormat';
import { Close, ContentPaste, MoreHoriz, RemoveRedEye } from '@mui/icons-material';
import { MaterialReactTable } from 'material-react-table';

const History = ({ onCopyToEditor }) => {
  const [newsletters, setNewsletters] = useState([]);
  const [loadingLetters, setLoadingLetters] = useState(true);
  const [error, setError] = useState(null);
  const [previewContent, setPreviewContent] = useState(null);
  const [status, setStatus] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);
  const [globalFilter, setGlobalFilter] = useState('');

  useEffect(() => {
    // Debounce the API call to avoid excessive requests during typing
    const handler = setTimeout(() => {
      const fetchNewsletters = async () => {
        setLoadingLetters(true);
        try {
          const response = await getNewsletterHistory(
            globalFilter,
            pagination.pageIndex + 1,
            pagination.pageSize
          );
          console.log('Newsletter history fetched:', response);
          const mappedData = response.data.map(item => ({
            ...item,
            newsletter_id: item.id,
          }));
          setNewsletters(mappedData);
          setRowCount(response.pagination.total);
          setError(null);
        } catch (error) {
          console.error('Error fetching newsletter history:', error);
          setError('Error fetching newsletter history');
        } finally {
          setLoadingLetters(false);
        }
      };

      fetchNewsletters();
    }, 300); // 300ms debounce delay

    // Cleanup the timeout to prevent multiple calls
    return () => clearTimeout(handler);
  }, [globalFilter, pagination.pageIndex, pagination.pageSize]); // Added globalFilter to dependencies

  const handlePreview = (content) => {
    setPreviewContent(content);
  };

  const handleClosePreview = () => {
    setPreviewContent(null);
  };

  const handlePopoverOpen = (event, recipients) => {
    setAnchorEl(event.currentTarget);
    setSelectedRecipients(recipients);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setSelectedRecipients([]);
  };

  const open = Boolean(anchorEl);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'subject',
        header: 'Subject',
        enableColumnActions: false,
      },
      {
        accessorKey: 'recipients',
        header: 'Recipients',
        size: 150,
        enableColumnActions: false,
        Cell: ({ cell }) => {
          const recipients = cell.getValue();
          return recipients.length <= 1 ? (
            recipients.join(', ')
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {recipients.slice(0, 1).join(', ')}
              <IconButton
                size="small"
                onClick={(event) => handlePopoverOpen(event, recipients)}
                sx={{ ml: 1 }}
              >
                <MoreHoriz />
              </IconButton>
            </Box>
          );
        },
      },
      {
        accessorKey: 'created_at',
        header: 'Sent At',
        size: 100,
        enableColumnActions: false,
        Cell: ({ cell }) => timeAgo(cell.getValue()),
      },
      {
        accessorKey: 'image_urls',
        header: 'Banner',
        size: 100,
        enableColumnActions: false,
        Cell: ({ cell }) => {
          const images = cell.getValue();
          return images.length > 0 ? (
            <a href={images[0]} target="_blank" rel="noopener noreferrer">
              <img
                src={images[0]}
                alt="Newsletter Image 1"
                style={{ maxWidth: '50px', maxHeight: '50px' }}
              />
            </a>
          ) : (
            'No image'
          );
        },
      },
      {
        accessorKey: 'content',
        header: 'Action',
        size: 100,
        enableColumnActions: false,
        Cell: ({ cell, row }) => (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => handlePreview(cell.getValue())}
            >
              <Tooltip title="Preview Newsletter" placement='top' arrow>
                <RemoveRedEye />
              </Tooltip>
            </IconButton>

            <IconButton
              size="small"
              onClick={() => onCopyToEditor(row.original)}
            >
              <Tooltip title="Copy to Editor" placement='top' arrow>
                <ContentPaste />
              </Tooltip>
            </IconButton>
          </Box>
        ),
      },
    ],
    [onCopyToEditor]
  );

  return (
    <Box>
      {status && (
        <CustomSnackbar
          open={!!status}
          message={status}
          severity={status.includes('successfully') ? 'success' : 'error'}
          onClose={() => setStatus('')}
        />
      )}

      {loadingLetters && (
        <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
          <CircularProgress size={24} />
        </Box>
      )}

      {error && (
        <Typography color="error" variant="body2" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}

      {newsletters.length === 0 ? (
        <Typography variant="body2">No newsletters found</Typography>
      ) : (
        <MaterialReactTable
          columns={columns}
          data={newsletters}
           manualFiltering
      enableGlobalFilter
          enableSorting={false}
          enableHiding={false}
          enableDensityToggle={false}
          enableRowNumbers={true}
          enableMultiSort={false}
          enableColumnFilters={false}
          onGlobalFilterChange={setGlobalFilter}
          globalFilter={globalFilter}
          manualPagination
          rowCount={rowCount}
          onPaginationChange={setPagination}
          state={{ pagination, isLoading: loadingLetters, globalFilter }}
          muiPaginationProps={{
            rowsPerPageOptions: [5, 10, 20, 50],
            showFirstButton: true,
            showLastButton: true,
          }}
          renderTopToolbarCustomActions={() => (
            <Typography variant="h6">All History</Typography>
          )}
          muiTablePaperProps={{
            elevation: 0,
          }}
          muiTableBodyRowProps={{
            sx: {
              backgroundColor: 'background.paper',
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              backgroundColor: 'background.paper',
            },
          }}
          muiTopToolbarProps={{
            sx: {
              backgroundColor: 'background.paper',
              borderBottom: '1px solid',
              borderColor: 'divider',
            },
          }}
          muiBottomToolbarProps={{
            sx: {
              backgroundColor: 'background.paper',
            },
          }}
        />
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box
          sx={{
            maxWidth: 300,
            maxHeight: 300,
            overflow: 'hidden',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              backgroundColor: 'background.paper',
              borderBottom: '1px solid',
              borderColor: 'divider',
              p: 1,
            }}
          >
            <Typography variant="body1">Recipients:</Typography>
            <Chip
              clickable
              onClick={() => {}}
              label={`${selectedRecipients.length} Recipients`}
              size="small"
            />
          </Stack>
          <Box
            sx={{
              maxHeight: 250,
              overflowY: 'auto',
              p: 1,
            }}
          >
            <Typography variant="body2" gutterBottom>
              {selectedRecipients.join(', ')}
            </Typography>
          </Box>
        </Box>
      </Popover>

      <Dialog
        open={!!previewContent}
        onClose={handleClosePreview}
        maxWidth="md"
        fullWidth
        hideBackdrop
      >
        <DialogTitle>
          Preview
        </DialogTitle>
        <IconButton onClick={handleClosePreview} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <Close />
        </IconButton>
        <DialogContent>
          <Typography variant="body1" mb={2}>
            Subject: {newsletters.find(n => n.content === previewContent)?.subject || 'No Subject'}
          </Typography>
          <div dangerouslySetInnerHTML={{ __html: previewContent }} />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default History;